import {Query} from './query';
import {ReverseContainsValuePropertyFilter} from './reverseContainsValuePropertyFilter';

export class QuerySort {

  constructor(field: string = 'creationDate', dir: 'asc' | 'desc' = 'desc') {
    this.field = field;
    this.dir = dir;
  }
  field: string;
  dir: string;

  static create(field: string = 'creationDate', dir: 'asc' | 'desc' = 'desc') {
    return new QuerySort(field, dir);
  }

  withField(field: string) {
    this.field = field;
    return this;
  }

  withDir(dir: 'asc' | 'desc') {
    this.dir = dir;
    return this;
  }
}

export class QueryFilter {
  field: string;
  operator: string;
  value: any;
  multipleValues: any[];
  multipleValuesType: string;
  logic: string;
  filters: QueryFilter[];

  static singleValue(field: string, operator: string, value: any) {
    return new QueryFilter()
      .withField(field)
      .withOperator(operator)
      .withValue(value);
  }

  static multipleValue(field: string, operator: string, multipleValues: any[], multipleValuesType: string) {
    return new QueryFilter()
      .withField(field)
      .withOperator(operator)
      .withMultipleValues(multipleValues)
      .withMultipleValuesType(multipleValuesType);
  }

  static group(filters: QueryFilter[], logic: string = 'and') {
    return new QueryFilter()
      .withFilters(filters)
      .withLogic(logic);
  }

  withField(field: string) {
    this.field = field;
    return this;
  }

  withOperator(operator: string) {
    this.operator = operator;
    return this;
  }

  withValue(value: any) {
    this.value = value;
    return this;
  }

  withMultipleValues(multipleValues: any[]) {
    this.multipleValues = multipleValues;
    return this;
  }

  withMultipleValuesType(multipleValuesType: string) {
    this.multipleValuesType = multipleValuesType;
    return this;
  }

  withLogic(logic: string) {
    this.logic = logic;
    return this;
  }

  withFilters(filters: QueryFilter[]) {
    this.filters = filters;
    return this;
  }
}

export class QueryUnion {
  filter: QueryFilter;
  join: QueryJoin;

  constructor(filter: QueryFilter = null, join: QueryJoin = null) {
    this.filter = filter;
    this.join = join;
  }

  static create(filter: QueryFilter = null, join: QueryJoin = null) {
    return new QueryUnion(filter, join);
  }

  withFilter(filter: QueryFilter) {
    this.filter = filter;
    return this;
  }

  withJoin(join: QueryJoin) {
    this.join = join;
    return this;
  }
}

export class QueryDTO {
  offset: number;
  limit: number;
  sort: QuerySort[];
  union: QueryUnion[];

  constructor(offset: number = 0, limit: number = 20, union: QueryUnion[] = [], sort: QuerySort[] = []) {
    this.offset = offset;
    this.limit = limit;
    this.union = union;
    this.sort = sort;
  }

  static create(offset: number = 0, limit: number = 20, union: QueryUnion[] = [], sort: QuerySort[] = []) {
    return new QueryDTO(offset, limit, union, sort);
  }

  withUnion(union: QueryUnion) {
    this.union.push(union);
    return this;
  }

  withOffset(offset: number) {
    this.offset = offset;
    return this;
  }

  withLimit(limit: number) {
    this.limit = limit;
    return this;
  }

  withSort(sorting: QuerySort) {
    this.sort = [sorting];
    return this;
  }

  withSorts(sorts: QuerySort[]) {
    this.sort = sorts;
    return this;
  }
}


export class QueryJoin {
  externalTable: string;
  internalTableKey: string;
  externalTableKey: string;
  filter: QueryFilter;

  constructor(externalTable: string = null, internalTableKey: string = null, externalTableKey: string = null, filter: QueryFilter = null) {
    this.externalTable = externalTable;
    this.internalTableKey = internalTableKey;
    this.externalTableKey = externalTableKey;
    this.filter = filter;
  }

  static create(externalTable: string = null, internalTableKey: string = null, externalTableKey: string = null, filter: QueryFilter = null) {
    return new QueryJoin(externalTable, internalTableKey, externalTableKey, filter);
  }

  withExternalTable(externalTable: string) {
    this.externalTable = externalTable;
    return this;
  }

  withInternalTableKey(internalTableKey: string) {
    this.internalTableKey = internalTableKey;
    return this;
  }

  withExternalTableKey(externalTableKey: string) {
    this.externalTableKey = externalTableKey;
    return this;
  }

  withFilter(filter: QueryFilter) {
    this.filter = filter;
    return this;
  }
}

export class QueryModel {
  dto: QueryDTO;
  url: string;

  static create(url: string): QueryModel {
    return new QueryModel(url);
  }

  constructor(url: string = '', dto: QueryDTO = null) {
    this.url = url;
    this.dto = dto;
  }

  withDTO(dto: QueryDTO) {
    this.dto = dto;
    return this;
  }
}

export class QueryTest {
  createQuery(): QueryModel {
    return QueryModel.create('/api/test').withDTO(
      QueryDTO.create()
        .withOffset(0)
        .withLimit(20)
        .withSort(new QuerySort().withField('creationDate').withDir('desc'))
        .withUnion(
          QueryUnion.create()
            .withFilter(QueryFilter.group(
              [
                QueryFilter.singleValue('title', 'contains', 'test'),
                QueryFilter.singleValue('customerId', 'eq', 5),
                QueryFilter.multipleValue('tags', 'contains', ['test', 'test2'], 'string')
              ],
            ))
            .withJoin(
              QueryJoin.create('customer', 'id', 'customerId', QueryFilter.singleValue('name', 'contains', 'test')))
            .withJoin(
              QueryJoin.create()
                .withFilter(QueryFilter.singleValue('name', 'contains', 'test'))
                .withExternalTable('customer')
                .withInternalTableKey('customerId')
                .withExternalTableKey('id')
            )
        ));
  }
}


export class QueryCreator {

  static convertQueryToFilter(query: Query, operand: string = 'and'): QueryFilter {
    const mainFilter: QueryFilter = new QueryFilter();
    mainFilter.logic = operand;
    mainFilter.filters = [];
    let activeFilters = 0;
    for (const propFilter of query.filters) {
      if (propFilter.active) {
        if (propFilter.property.type === 'flag') {
          const enumFilter = propFilter as ReverseContainsValuePropertyFilter;
          if (enumFilter.queryArray.length === 0) {
            continue;
          } else if (enumFilter.queryArray.length === 1 && enumFilter.queryArray[0] === -1) {
            const unflaggedFilter = new QueryFilter();
            unflaggedFilter.field = 'flagged';
            unflaggedFilter.value = false;
            unflaggedFilter.operator = 'eq';
            mainFilter.filters.push(unflaggedFilter);
            activeFilters++;
          } else if (enumFilter.queryArray.length > 1 && enumFilter.queryArray.includes(-1)) {
            const rootFilter = new QueryFilter();
            rootFilter.logic = 'or';
            const unflaggedFilter = new QueryFilter();
            unflaggedFilter.field = 'flagged';
            unflaggedFilter.value = false;
            unflaggedFilter.operator = 'eq';
            const rootFlaggedFilter = new QueryFilter();
            rootFlaggedFilter.logic = 'and';
            const flaggedFilter = new QueryFilter();
            flaggedFilter.field = 'flagged';
            flaggedFilter.value = true;
            flaggedFilter.operator = 'eq';
            const flagColorFilter = new QueryFilter();
            flagColorFilter.field = 'flagColor';
            flagColorFilter.multipleValues = enumFilter.queryArray;
            flagColorFilter.multipleValuesType = enumFilter.property.objectType;
            flagColorFilter.operator = 'reversecontains';
            rootFlaggedFilter.filters = [flaggedFilter, flagColorFilter];
            rootFilter.filters = [rootFlaggedFilter, unflaggedFilter];
            mainFilter.filters.push(rootFilter);
            activeFilters++;
          } else {
            const rootFlaggedFilter = new QueryFilter();
            rootFlaggedFilter.logic = 'and';
            const flaggedFilter = new QueryFilter();
            flaggedFilter.field = 'flagged';
            flaggedFilter.value = true;
            flaggedFilter.operator = 'eq';
            const flagColorFilter = new QueryFilter();
            flagColorFilter.field = 'flagColor';
            flagColorFilter.multipleValues = enumFilter.queryArray;
            flagColorFilter.multipleValuesType = enumFilter.property.objectType;
            flagColorFilter.operator = 'reversecontains';
            rootFlaggedFilter.filters = [flaggedFilter, flagColorFilter];
            mainFilter.filters.push(rootFlaggedFilter);
            activeFilters++;
          }
        } else {
          mainFilter.filters.push(propFilter.toQueryFilter());
          activeFilters++;
        }
      }
    }

    return activeFilters > 0 ? mainFilter : null;
  }

  static generateFilter(query: Query, sorting: QuerySort, entriesToFetch: number, fetchOffset: number, baseOperand: string = 'and', join: QueryJoin = undefined): QueryDTO {
    return new QueryDTO()
      .withUnion(new QueryUnion(QueryCreator.convertQueryToFilter(query, baseOperand), join))
      .withOffset(fetchOffset)
      .withLimit(entriesToFetch)
      .withSort(sorting);
  }
}
